import { BehaviorSubject, Observable } from 'rxjs';
import { IHttpParams } from '../interfaces';
import { HttpParams } from '@angular/common/http';

const DEFAULT_HTTP_PARAMS: IHttpParams = {
  pageNumber: 0,
  pageSize: 50,
  fromSmallerToLarger: true,
};

export interface IParamsHandler<P extends IHttpParams = IHttpParams> {
  readonly params$: Observable<P>;
  readonly params: P;
  readonly isFirstPage: boolean;
  readonly isLastPage: boolean;

  total: number;

  toNextPage(): void;
  toPreviousPage(): void;
  toFirstPage(): void;
  toLastPage(): void;
  toPage(pageIndex: number): void;
  changePageSize(pageSize: number): void;
  patchParams(params: Partial<P>): void;
  isParamsChanged(params: Partial<P>): boolean;
}

/**
 * A class that handles HTTP parameters for pagination and filtering.
 * @template P - The type of HTTP parameters.
 */
export class ParamsHandler<P extends IHttpParams = IHttpParams>
  implements IParamsHandler<P>
{
  _params$: BehaviorSubject<P>;
  _total = 0;
  params$: Observable<P>;

  /**
   * Gets the current HTTP parameters.
   * @returns The current HTTP parameters, or null if none are set.
   */
  get params(): any | null {
    return this._params$.value;
  }

  /**
   * Returns a boolean indicating whether the current page is the first page.
   * @returns {boolean} A boolean indicating whether the current page is the first page.
   */
  get isFirstPage(): boolean {
    return this.params?.page === 1;
  }

  /**
   * Determines whether the current page is the last page of results.
   * @returns {boolean} True if the current page is the last page, false otherwise.
   */
  get isLastPage(): boolean {
    return this.params?.page_size === 0
      ? true
      : this.params!.page * this.params!.page_size >= this.total;
  }

  /**
   * Setter for the total number of items in the collection.
   * If the current page is greater than the last page, navigates to the last page.
   * @param value The total number of items in the collection.
   */
  set total(value: number) {
    this._total = value;
    if (this.params!.page > Math.ceil(this._total / this.params!.page_size)) {
      this.toLastPage();
    }
  }

  /**
   * Gets the total number of items in the response.
   * @returns The total number of items.
   */
  get total(): number {
    return this._total;
  }

  constructor(params: P = <P>DEFAULT_HTTP_PARAMS) {
    this._params$ = new BehaviorSubject<P>(params);
    this.params$ = this._params$.asObservable();
  }

  /**
   * Moves to the next page if it exists.
   * If the current page is the last page, this method does nothing.
   */
  toNextPage(): void {
    if (!this.isFirstPage) {
      this.toPage(this.params!.page + 1);
    }
  }

  /**
   * Navigates to the previous page of results, if there is one.
   * If the current page is the first page, this method does nothing.
   */
  toPreviousPage(): void {
    if (!this.isFirstPage) {
      this.toPage(this.params!.page - 1);
    }
  }

  /**
   * Sets the current page to the first page and updates the HTTP params accordingly.
   */
  toFirstPage(): void {
    this.toPage(0);
  }

  /**
   * Navigates to the last page of results based on the current `total` and `page_size` parameters.
   */
  toLastPage(): void {
    const pagesCount = Math.ceil(this.total / this.params!.page_size);
    this.toPage(pagesCount);
  }

  /**
   * Updates the `pageNumber` parameter in the HTTP request parameters to the specified value.
   * @param pageIndex The new page number to set.
   */
  toPage(pageIndex: number): void {
    this.patchParams(<Partial<P>>{ pageNumber: pageIndex });
  }

  /**
   * Updates the page size parameter and resets the page number to 1.
   * @param pageSize - The new page size value.
   */
  changePageSize(pageSize: number): void {
    this.patchParams(<Partial<P>>{ pageNumber: 1, pageSize: pageSize });
  }

  /**
   * Updates the current HTTP params with the provided partial params object.
   * If the new params are different from the current params, the `_params$` subject is updated.
   * @param params - The partial params object to merge with the current params.
   * @returns void
   */
  patchParams(params: Partial<P>): void {
    if (this.params && this.isParamsChanged(params)) {
      this._params$.next({ ...this.params, ...params });
    }
  }

  /**
   * Checks if any of the provided parameters have changed from their previous value.
   * @param params - The parameters to check for changes.
   * @returns True if any of the parameters have changed, false otherwise.
   */
  isParamsChanged(params: Partial<P>): boolean {
    return Object.keys(params).some(
      paramsName =>
        this.params![paramsName as keyof IHttpParams] !=
        params[paramsName as keyof IHttpParams]
    );
  }
}

/**
 * Removes null, undefined and empty array values from an object.
 * @param params - The object to shake.
 * @returns The object without null, undefined and empty array values.
 */
export function shakeParams(params: any): any {
  Object.keys(params).forEach(p => {
    if (params[p] === null || params[p] === undefined) {
      delete params[p];
    }

    if (Array.isArray(p) && !p.length) {
      delete params[p];
    }
  });

  return new HttpParams().appendAll(params);

  // return params;
}
